import { useState, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Upload, FileSpreadsheet } from "lucide-react";

export default function ImportCsvModal({ open, onOpenChange }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  const qc = useQueryClient();

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} leads`);
      qc.invalidateQueries();
      setFile(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="import-csv-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Import leads from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            CSV columns supported: <span className="font-mono text-xs">name, phone, email, company, source, notes, deal_value</span>
          </p>
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-border hover:border-primary/50 py-10 flex flex-col items-center gap-2 transition-colors"
            data-testid="csv-dropzone"
          >
            <FileSpreadsheet className="h-8 w-8 text-primary" />
            <span className="text-sm font-medium">{file ? file.name : "Click to choose a CSV file"}</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="csv-file-input"
          />
          <Button
            onClick={submit}
            disabled={!file || loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="csv-import-submit"
          >
            <Upload className="h-4 w-4 mr-2" />
            {loading ? "Importing..." : "Import leads"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
